import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import './Courses.css';

const demoCategories = [
  { id: 1, name: "Spiritual Growth" },
  { id: 2, name: "Health & Wellness" },
  { id: 3, name: "Wealth & Leadership" },
];

const demoCourses = [
  { id: "demo-mindfulness", title: "Mindful Living Foundations", short_description: "Build calm, focus, and a sustainable daily mindfulness practice through guided lessons.", category_id: 1, category_name: "Spiritual Growth", instructor_name: "Dr. Amara Lewis", level: "beginner", duration_hours: 6, video_count: 18, is_free: true, is_featured: true, thumbnail_url: "/course-images/mindfulness.jpg", created_at: "2026-09-10" },
  { id: "demo-purpose", title: "Purpose, Vision & Personal Growth", short_description: "Clarify your values, create an inspiring vision, and turn meaningful goals into action.", category_id: 1, category_name: "Spiritual Growth", instructor_name: "Marcus Adeyemi", level: "intermediate", duration_hours: 8, video_count: 22, price: 79, sale_price: 49, is_featured: false, thumbnail_url: "/course-images/leadership.jpg", created_at: "2026-09-08" },
  { id: "demo-wellness", title: "Whole-Body Wellness Blueprint", short_description: "A practical system for nutrition, energy, recovery, and healthier everyday routines.", category_id: 2, category_name: "Health & Wellness", instructor_name: "Nia Thompson, RD", level: "beginner", duration_hours: 7, video_count: 20, price: 59, is_featured: true, thumbnail_url: "/course-images/wellness.jpg", created_at: "2026-09-06" },
  { id: "demo-yoga", title: "Yoga for Strength and Balance", short_description: "Progressive movement sessions designed to improve mobility, strength, and confidence.", category_id: 2, category_name: "Health & Wellness", instructor_name: "Maya Carter", level: "intermediate", duration_hours: 5, video_count: 16, price: 45, thumbnail_url: "/course-images/yoga.jpg", created_at: "2026-09-04" },
  { id: "demo-finance", title: "Financial Freedom Essentials", short_description: "Master budgeting, saving, credit, and investing with a clear step-by-step roadmap.", category_id: 3, category_name: "Wealth & Leadership", instructor_name: "Jordan Williams", level: "beginner", duration_hours: 9, video_count: 26, price: 89, sale_price: 69, is_featured: true, thumbnail_url: "/course-images/finance.jpg", created_at: "2026-09-02" },
  { id: "demo-business", title: "Build a Purpose-Driven Business", short_description: "Shape your offer, attract the right audience, and create a business built for durable growth.", category_id: 3, category_name: "Wealth & Leadership", instructor_name: "Aisha Bennett", level: "advanced", duration_hours: 12, video_count: 34, price: 129, thumbnail_url: "/course-images/business.jpg", created_at: "2026-08-30" },
];

const levelFilters = [
  { label: 'All levels', value: 'all' },
  { label: 'Beginner', value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' },
];

const sortOptions = [
  { label: 'Newest', value: 'newest' },
  { label: 'Most lessons', value: 'lessons' },
  { label: 'Price: Low to High', value: 'price-asc' },
  { label: 'Price: High to Low', value: 'price-desc' },
];

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [levelFilter, setLevelFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [coursesRes, categoriesRes] = await Promise.all([
          api.get('/courses'),
          api.get('/categories'),
        ]);

        if (isMounted) {
          setCourses(coursesRes.data);
          setCategories(categoriesRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted) {
          setCourses(demoCourses);
          setCategories(demoCategories);
          setIsDemo(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const categoryCounts = useMemo(() => {
    return courses.reduce((acc, course) => {
      if (!course.category_id) return acc;
      const key = String(course.category_id);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [courses]);

  const filteredCourses = useMemo(() => {
    let filtered = [...courses];

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        (course) => String(course.category_id) === selectedCategory
      );
    }

    if (levelFilter !== 'all') {
      filtered = filtered.filter(
        (course) => (course.level || 'beginner').toLowerCase() === levelFilter
      );
    }

    if (searchTerm.trim()) {
      const query = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((course) =>
        [
          course.title,
          course.short_description,
          course.description,
          course.category_name,
          course.instructor_name,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      );
    }

    return filtered.sort((a, b) => sortCourses(a, b, sortBy));
  }, [courses, selectedCategory, levelFilter, searchTerm, sortBy]);

  const stats = useMemo(() => {
    const totalHours = filteredCourses.reduce(
      (sum, course) => sum + Number(course.duration_hours || 0),
      0
    );
    const totalLessons = filteredCourses.reduce(
      (sum, course) => sum + Number(course.video_count || 0),
      0
    );
    const instructors = filteredCourses.reduce((set, course) => {
      if (course.instructor_name) {
        set.add(course.instructor_name);
      }
      return set;
    }, new Set());

    return {
      totalHours,
      totalLessons,
      instructors: instructors.size,
    };
  }, [filteredCourses]);

  const featuredCategory = useMemo(() => {
    if (selectedCategory !== 'all') {
      const selected = categories.find(
        (cat) => String(cat.id) === selectedCategory
      );
      if (selected) {
        return {
          ...selected,
          count: filteredCourses.length,
        };
      }
    }

    if (!categories.length) {
      return null;
    }

    return [...categories]
      .map((category) => ({
        ...category,
        count: categoryCounts[String(category.id)] || 0,
      }))
      .sort((a, b) => b.count - a.count)[0];
  }, [categories, categoryCounts, filteredCourses.length, selectedCategory]);

  const selectedCategoryName =
    selectedCategory === 'all'
      ? 'All categories'
      : categories.find((cat) => String(cat.id) === selectedCategory)?.name ||
        'Selected category';

  const resultsSummary =
    filteredCourses.length === 0
      ? 'No courses match your filters yet'
      : `Showing ${filteredCourses.length} course${
          filteredCourses.length === 1 ? '' : 's'
        } • ${stats.totalLessons} lesson${
          stats.totalLessons === 1 ? '' : 's'
        } in ${selectedCategoryName.toLowerCase()}`;

  const hasActiveFilters =
    selectedCategory !== 'all' || levelFilter !== 'all' || searchTerm.trim();

  const resetFilters = () => {
    setSelectedCategory('all');
    setLevelFilter('all');
    setSearchTerm('');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page page-courses">
      <div className="courses-container">
        <section className="courses-hero">
          <div className="hero-content">
            <p className="eyebrow">{isDemo ? 'Demo Learning Library' : 'AKU Learning Library'}</p>
            <h1>Learn without limits</h1>
            <p className="hero-subtitle">
              Build practical skills with curated programs led by practitioners.
              {` `}
              {selectedCategoryName} currently features{' '}
              <strong>{filteredCourses.length}</strong> active course
              {filteredCourses.length === 1 ? '' : 's'}.
            </p>

            <div className="hero-search">
              <div className="search-input">
                <SearchIcon />
                <input
                  type="text"
                  aria-label="Search courses"
                  placeholder="Search by title, instructor, or keyword"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="clear-search"
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <span className="results-meta">{resultsSummary}</span>
            </div>

            <div className="hero-stats">
              <div className="stat-card">
                <span className="stat-value">{filteredCourses.length}</span>
                <span className="stat-label">Courses</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.totalLessons}</span>
                <span className="stat-label">Lessons</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {stats.totalHours > 0
                    ? `${stats.totalHours}h`
                    : 'Self-paced'}
                </span>
                <span className="stat-label">
                  {stats.totalHours > 0 ? 'Guided hours' : 'Flexible learning'}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-value">
                  {stats.instructors || (filteredCourses.length ? 1 : 0)}
                </span>
                <span className="stat-label">Instructors</span>
              </div>
            </div>
          </div>

          {featuredCategory && (
            <div className="hero-sidecard">
              <p className="side-label">
                {selectedCategory === 'all'
                  ? 'Popular track'
                  : 'Focused collection'}
              </p>
              <h3>{featuredCategory.name}</h3>
              <p className="sidecard-description">
                {featuredCategory.count > 0
                  ? `${featuredCategory.count} curated course${
                      featuredCategory.count === 1 ? '' : 's'
                    } waiting for you.`
                  : 'New category launching soon.'}
              </p>
              <ul className="sidecard-points">
                <li>Structured outcomes</li>
                <li>Instructor support</li>
                <li>Certificate ready</li>
              </ul>
              {selectedCategory === 'all' ? (
                <button
                  type="button"
                  className="hero-cta"
                  onClick={() =>
                    setSelectedCategory(String(featuredCategory.id))
                  }
                >
                  Explore {featuredCategory.name}
                </button>
              ) : (
                <button
                  type="button"
                  className="hero-cta ghost"
                  onClick={() => setSelectedCategory('all')}
                >
                  Reset category filter
                </button>
              )}
            </div>
          )}
        </section>

        <section className="courses-filters">
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="sort-select">Sort by</label>
              <select
                id="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group level-group">
              <label>Level</label>
              <div className="pill-group">
                {levelFilters.map((level) => (
                  <button
                    key={level.value}
                    type="button"
                    className={`pill ${
                      levelFilter === level.value ? 'active' : ''
                    }`}
                    onClick={() => setLevelFilter(level.value)}
                  >
                    {level.label}
                  </button>
                ))}
              </div>
            </div>

            {hasActiveFilters && (
              <button type="button" className="link-button" onClick={resetFilters}>
                Clear filters
              </button>
            )}
          </div>

          <div
            className="category-pills"
            role="group"
            aria-label="Filter courses by category"
          >
            <button
              type="button"
              className={`pill ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              All categories
              <span className="pill-count">{courses.length}</span>
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={`pill ${
                  selectedCategory === String(category.id) ? 'active' : ''
                }`}
                onClick={() => setSelectedCategory(String(category.id))}
              >
                {category.name}
                {categoryCounts[String(category.id)] ? (
                  <span className="pill-count">
                    {categoryCounts[String(category.id)]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </section>

        {filteredCourses.length === 0 ? (
          <div className="empty-state elevated">
            <h3>No courses match your filters</h3>
            <p>
              Try adjusting your search or resetting the filters to see our full
              catalog.
            </p>
            <button type="button" className="btn-primary" onClick={resetFilters}>
              Reset filters
            </button>
          </div>
        ) : (
          <div className="courses-grid">
            {filteredCourses.map((course) => {
              const price = formatPrice(course);
              const description =
                course.short_description || course.description || '';

              return (
                <article key={course.id} className="course-card">
                  <div className="course-thumb">
                    {course.thumbnail_url ? (
                      <img src={course.thumbnail_url} alt={course.title} />
                    ) : (
                      <div className="thumb-placeholder">
                        {getInitials(course.title)}
                      </div>
                    )}
                    <span className="course-level">
                      {formatLevel(course.level)}
                    </span>
                  </div>

                  <div className="course-body">
                    <div className="course-top">
                      <span className="course-category">
                        {course.category_name || 'General'}
                      </span>
                      {course.is_featured && (
                        <span className="course-badge">Featured</span>
                      )}
                    </div>
                    <h3>
                      <Link to={`/courses/${course.id}`}>{course.title}</Link>
                    </h3>
                    <p className="course-description">{description}</p>

                    <div className="course-meta">
                      <span>{formatDuration(course)}</span>
                      <span>{Number(course.video_count || 0)} lessons</span>
                      <span>
                        {course.instructor_name || 'Expert instructor'}
                      </span>
                    </div>
                  </div>

                  <div className="course-footer">
                    <div className="course-price">
                      <span className="price-current">{price.primary}</span>
                      {price.secondary && (
                        <span className="price-original">{price.secondary}</span>
                      )}
                    </div>
                    <Link to={`/courses/${course.id}`} className="course-link">
                      View details
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const sortCourses = (a, b, sortBy) => {
  const getPriceValue = (course) =>
    Number(course.sale_price || course.price || 0);

  switch (sortBy) {
    case 'price-asc':
      return getPriceValue(a) - getPriceValue(b);
    case 'price-desc':
      return getPriceValue(b) - getPriceValue(a);
    case 'lessons':
      return Number(b.video_count || 0) - Number(a.video_count || 0);
    case 'newest':
    default:
      return (
        new Date(b.created_at || b.updated_at || 0) -
        new Date(a.created_at || a.updated_at || 0)
      );
  }
};

const formatLevel = (level) => {
  if (!level) return 'Beginner';
  const normalized = level.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatDuration = (course) => {
  const hours = Number(course.duration_hours || 0);
  if (hours > 0) {
    return `${hours} hr${hours > 1 ? 's' : ''}`;
  }

  const lessons = Number(course.video_count || 0);
  if (lessons > 0) {
    return `${lessons} lesson${lessons > 1 ? 's' : ''}`;
  }

  return 'Self-paced';
};

const formatPrice = (course) => {
  if (course.is_free) {
    return { primary: 'Free' };
  }

  const price = Number(course.price || 0);
  const salePrice = Number(course.sale_price || 0);

  if (salePrice > 0 && salePrice < price) {
    return {
      primary: `$${salePrice.toFixed(2)}`,
      secondary: `$${price.toFixed(2)}`,
    };
  }

  if (price === 0) {
    return { primary: 'Free' };
  }

  return {
    primary: `$${price.toFixed(2)}`,
  };
};

const getInitials = (value = '') => {
  return value
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
};

const SearchIcon = () => (
  <svg
    className="search-icon"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 21L16.65 16.65"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default Courses;
